import Card from "@/components/card"
import ListItem from "@/components/list-item"
import Link from "@/components/link"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faInfoCircle, faUndo } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useState } from "react"
import RiskScaleSection from "../../../../adm/entities/account/ui/RiskScaleSection"
import MultiThresholdSlider from "@/components/MultiThresholdSlider"
import {
  DEFAULT_RISK_SCALES,
  DEFAULT_RPS_SCALES,
  RiskResidualItem,
  RiskScaleItem
} from "@/features/risks/entities/risk/model/risk.types"
import { sortScaleItems } from "@/features/risks/entities/risk/utils/sortScales"
import Tooltip from "@/components/tooltip"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import RiskTypeChip from "./RiskTypeChip"

export type EditableScaleKey =
  | "severityScale"
  | "frequencyScale"
  | "preventionScale"
  | "rpsSeverityScale"
  | "rpsFrequencyScale"
  | "rpsPreventionScale"

type Props = {
  riskType?: "phy" | "rps"
  isPlain?: boolean
  onDirty?: () => void
}

// Map scale base name to actual key based on riskType
const getScaleKey = (base: "severity" | "frequency" | "prevention", riskType: string = "phy"): EditableScaleKey => {
  if (riskType === "rps") {
    return `rps${base.charAt(0).toUpperCase()}${base.slice(1)}Scale` as EditableScaleKey
  }
  return `${base}Scale` as EditableScaleKey
}

const getResidualKey = (riskType: string = "phy"): "residualRiskScale" | "rpsResidualRiskScale" => {
  return riskType === "rps" ? "rpsResidualRiskScale" : "residualRiskScale"
}

export default function RiskOrganizationScalesConfig({ riskType = "phy", isPlain = false, onDirty }: Props) {
  const dispatch = useAppDispatch()
  const Wrapper = isPlain ? "div" : Card

  const draft = useAppSelector(organizationStore.selectors.draft)

  const [isOpen, setIsOpen] = useState(false)

  // Get the appropriate scale keys based on riskType
  const severityKey = getScaleKey("severity", riskType)
  const frequencyKey = getScaleKey("frequency", riskType)
  const preventionKey = getScaleKey("prevention", riskType)
  const residualKey = getResidualKey(riskType)

  const severityScale = draft[severityKey] ?? []
  const frequencyScale = draft[frequencyKey] ?? []
  const residualScale = draft[residualKey] ?? []

  const residualMax = Math.max(...severityScale.map((s) => s.value)) * Math.max(...frequencyScale.map((s) => s.value))

  const adjustedResidualScale = residualScale.map((s, i, arr) =>
    i === arr.length - 1 ? { ...s, max: residualMax } : s
  )

  const handleReset = () => {
    const defaults = riskType === "rps" ? DEFAULT_RPS_SCALES : DEFAULT_RISK_SCALES
    const { severityScale, frequencyScale, preventionScale, residualRiskScale } = defaults

    dispatch(
      organizationStore.actions.edit({
        key: severityKey,
        value: sortScaleItems([...severityScale])
      } as any)
    )

    dispatch(
      organizationStore.actions.edit({
        key: frequencyKey,
        value: sortScaleItems([...frequencyScale])
      } as any)
    )

    dispatch(
      organizationStore.actions.edit({
        key: preventionKey,
        value: sortScaleItems([...preventionScale])
      } as any)
    )

    dispatch(
      organizationStore.actions.edit({
        key: residualKey,
        value: [...residualRiskScale]
      } as any)
    )
    onDirty?.()
  }

  const updateScale = (scaleKey: EditableScaleKey, updater: (items: RiskScaleItem[]) => RiskScaleItem[]) => {
    const current = draft[scaleKey] as RiskScaleItem[] | undefined
    const next = sortScaleItems(updater([...(current ?? [])]))

    dispatch(
      organizationStore.actions.edit({
        key: scaleKey,
        value: next
      } as any)
    )

    onDirty?.()
  }

  const handleEditValue = (scaleKey: EditableScaleKey, index: number, newValue: number) => {
    updateScale(scaleKey, (items) => items.map((item, i) => (i === index ? { ...item, value: newValue } : item)))
  }

  const handleEditLabel = (scaleKey: EditableScaleKey, index: number, newLabel: string) => {
    updateScale(scaleKey, (items) => items.map((item, i) => (i === index ? { ...item, label: newLabel } : item)))
  }

  const handleAddItem = (scaleKey: EditableScaleKey, index: number) => {
    updateScale(scaleKey, (items) => {
      const next = [...items]
      next.splice(index + 1, 0, {
        label: t("utils_new"),
        value: 1
      })
      return next
    })
  }

  const handleRemoveItem = (scaleKey: EditableScaleKey, index: number) => {
    updateScale(scaleKey, (items) => {
      if (items.length <= 1) return items
      return items.filter((_, i) => i !== index)
    })
  }

  const handleEditResidual = (newScale: RiskResidualItem[]) => {
    dispatch(
      organizationStore.actions.edit({
        key: residualKey,
        value: newScale
      } as any)
    )
    onDirty?.()
  }

  return (
    <Wrapper className="p-4 space-y-6">
      <div className={`flex justify-between items-center ${isOpen ? "mb-4" : ""}`}>
        <div className="flex flex-col items-center cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
          <div className="flex items-center gap-3">
            <RiskTypeChip isRps={riskType === "rps"} showCount={false} showLabel={false} disableTooltip isSmall />
            <span className="text-xl font-bold">
              {t(`Configuration des barèmes de risques${riskType === "rps" ? " RPS" : ""}`)}
            </span>
            <Tooltip
              width={200}
              text={
                riskType === "rps"
                  ? "Échelles d'évaluation des risques psychosociaux : Gravité (impact sur le bien-être), Fréquence (exposition), Prévention (mesures en place), et Risque résiduel (score final)."
                  : "Échelles d'évaluation des risques professionnels : Gravité (sévérité de l'accident), Fréquence (probabilité), Prévention (mesures de contrôle), et Risque résiduel (score d'exposition final)."
              }
              position="right">
              <FontAwesomeIcon icon={faInfoCircle} className="grey-t ml-2 cursor-help" />
            </Tooltip>
          </div>
        </div>

        <Space />

        <Link icon={faUndo} onClick={handleReset}>
          {t("Réinitialiser les valeurs par défaut")}
        </Link>

        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className="grey-t cursor-pointer ml-2"
          onClick={() => setIsOpen((v) => !v)}
        />
      </div>

      {isOpen && (
        <>
          <RiskScaleSection
            title={t("risk_item_g")}
            scaleKey={severityKey}
            items={severityScale}
            onEditValue={handleEditValue}
            onEditLabel={handleEditLabel}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
          />

          <RiskScaleSection
            title={t("risk_item_f")}
            scaleKey={frequencyKey}
            items={frequencyScale}
            onEditValue={handleEditValue}
            onEditLabel={handleEditLabel}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
          />

          <RiskScaleSection
            title={t("risk_item_p")}
            scaleKey={preventionKey}
            items={draft[preventionKey] ?? []}
            onEditValue={handleEditValue}
            onEditLabel={handleEditLabel}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
          />

          <ListItem>
            <div className="flex-col w-full">
              <h3 className="font-bold mb-4">{t("risk_item_rr")}</h3>

              <MultiThresholdSlider
                min={1}
                max={residualMax}
                scale={adjustedResidualScale}
                chipsOrientation="col"
                onChange={handleEditResidual}
              />
            </div>
          </ListItem>
        </>
      )}
    </Wrapper>
  )
}
